/**
 * The modifier language, and the rules that keep a build reproducible.
 *
 * An attachment does not "change a stat". It contributes *modifiers*, each of which
 * names a path into the assembled weapon, a kind, and a value. The resolver then
 * evaluates every path in one fixed order:
 *
 *     base  ->  additive flat  ->  additive percent  ->  multiplicative percent
 *           ->  clamp  ->  derived
 *
 * Two consequences are worth stating plainly, because both were the point:
 *
 *  1. **The order is in the code, not in the data.** A build of five attachments
 *     resolves to the same numbers whatever order the player equipped them in, and
 *     whatever order the catalog happens to list them in. The only ordering that
 *     survives into the data is `SLOT_ORDER`, which breaks ties between two `set`
 *     modifiers that name the same path — and that ordering is fixed here.
 *
 *  2. **Additive and multiplicative are different kinds, never inferred.** Three
 *     parts that each say "-10% ADS time" are -30% if they are additive and -27.1%
 *     if they are multiplicative, and a system that guesses from the sign or the
 *     magnitude will pick differently on different days. `mult` sums into one
 *     `(1 + Sum)` factor; `scale` multiplies. An attachment says which it is.
 *
 * Nothing here writes to `SCRAPED` or to the base weapon table. The resolver copies
 * the assembled weapon into a fresh object and modifies that copy, so the scraped /
 * derived / authored separation in `weapons.stats.js` survives intact — an
 * attachment can change what the AK-27 does in play without ever having changed what
 * the reference said about it.
 */

/* -------------------------------------------------------------------------- */
/* slots                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The eleven slots, in resolution order.
 *
 * This order is load-bearing in exactly one place: when two attachments both `set`
 * the same path (a fire-mode conversion in `fireMod` and a mastery conversion that
 * also lives in `fireMod` cannot both apply, but a `magazine` mastery and a `fireMod`
 * conversion can), the later slot wins. Later is more specialised: `fireMod` is the
 * last word on how the trigger behaves, and it sits last.
 */
export const SLOT_ORDER = [
  'optic',
  'muzzle',
  'barrel',
  'underbarrel',
  'stockPad',
  'magazine',
  'comb',
  'rearGrip',
  'stock',
  'laser',
  'fireMod',
];

/** Slot -> human label, for the gunsmith screen. */
export const SLOT_LABEL = {
  optic: 'OPTIC',
  muzzle: 'MUZZLE',
  barrel: 'BARREL',
  underbarrel: 'UNDERBARREL',
  stockPad: 'BUTT PAD',
  magazine: 'MAGAZINE',
  comb: 'COMB',
  rearGrip: 'REAR GRIP',
  stock: 'STOCK',
  laser: 'LASER',
  fireMod: 'FIRE MOD',
};

export const SLOT_INDEX = Object.fromEntries(SLOT_ORDER.map((s, i) => [s, i]));

/* -------------------------------------------------------------------------- */
/* modifier kinds                                                              */
/* -------------------------------------------------------------------------- */

/**
 * `add`   flat, in the path's own units. `magSize +10`, `burstCount +1`.
 * `mult`  additive percent. Every `mult` on a path sums, then applies once as
 *         `(1 + Sum)`. This is the kind almost every ordinary part uses.
 * `scale` multiplicative percent. Applies as a product, after all the `mult`s.
 *         Reserved for parts that should compound rather than dilute — mastery
 *         conversions, and the handful of strong compound parts.
 * `set`   replace outright. Behaviour, not magnitude: fire mode, pellet count,
 *         a suppressor switching the minimap signature off, an overheat block.
 */
export const MOD_KINDS = ['add', 'mult', 'scale', 'set'];

/** Terse constructors, so a catalog entry reads as a sentence. */
export const add = (path, value) => ({ path, kind: 'add', value });
export const mult = (path, value) => ({ path, kind: 'mult', value });
export const scale = (path, value) => ({ path, kind: 'scale', value });
export const set = (path, value) => ({ path, kind: 'set', value });

/* -------------------------------------------------------------------------- */
/* the addressable surface                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Every path an attachment is allowed to name, and what it means.
 *
 * A path outside this table is refused by `validateMod` rather than silently doing
 * nothing, because a typo'd path is otherwise invisible: the build resolves, the
 * numbers look plausible, and the part the player equipped simply has no effect.
 *
 * Four of these are *virtual* — they do not exist on the assembled weapon and are
 * consumed by the derive stage instead:
 *
 *   `damage.mult`    scales every damage value on the curve.
 *   `damage.range`   scales every distance on the curve.
 *   `reload.scale`   scales every stage of the reload.
 *   `recoil.climb30Deg` / `recoil.yawRatio` are real fields on the def but are
 *                    *budgets*: the derive stage turns them back into
 *                    `recoil.pitch` / `recoil.yaw` against the weapon's own pattern
 *                    table. This is the only way an attachment may touch recoil
 *                    magnitude, and it is why no attachment can reshape a pattern.
 */
export const PATHS = {
  /* ------------------------------ fire control ---------------------------- */
  rpm: 'rounds per minute (in-burst, for burst weapons)',
  fireMode: "'auto' | 'semi' | 'burst' | 'pump' | 'bolt'",
  burstCount: 'rounds per trigger pull, burst mode only',
  burstDelay: 's of dead time after a burst',
  pumpTime: 's to work a pump',
  boltCycle: 's to work a bolt',
  magSize: 'rounds in a magazine',
  pellets: 'projectiles per trigger pull',
  pelletSpread: 'rad, half-angle of the shell pattern',
  pelletSpreadAdsScale: 'the pattern tightens to this fraction at full ADS',

  /* -------------------------------- damage -------------------------------- */
  'damage.mult': 'virtual — scales the damage axis of the curve',
  'damage.range': 'virtual — scales the distance axis of the curve',
  muzzleVelocity: 'm/s',
  penetration: 'penetration budget, in reference metres',

  /* -------------------------------- timing -------------------------------- */
  'reload.scale': 'virtual — scales every reload stage',
  'ads.in': 's to shoulder',
  'ads.out': 's to lower',

  /* -------------------------------- accuracy ------------------------------ */
  // `spread.ads` is deliberately absent. It is exactly zero for all 23 weapons and
  // `deriveEffective` pins it there; an attachment that could touch it would be able
  // to break the one property the whole accuracy model rests on.
  'spread.adsMoving': 'rad, cone while aimed and moving',
  'spread.hip': 'rad, cone at the hip, stationary',
  'spread.hipMoving': 'rad, cone at the hip, moving',
  'spread.hipSprint': 'rad, cone at the hip, sprinting',
  'spread.crouchScale': 'cone multiplier while crouched',
  'spread.airScale': 'cone multiplier while airborne',
  'spread.maxBloom': 'rad, ceiling on accumulated bloom',
  'spread.bloomDecay': 'rad/s, bloom decay',
  'spread.bloomGain': 'rad, *net* bloom per shot after one shot of decay',

  /* --------------------------------- recoil ------------------------------- */
  'recoil.climb30Deg': 'deg the muzzle walks over 30 rounds — the magnitude budget',
  'recoil.yawRatio': 'yaw magnitude as a fraction of pitch',
  'recoil.random': 'noise as a fraction of each shot',
  'recoil.kick': 'view-kick impulse',
  'recoil.recovery': '1/s, how fast the view settles',
  'recoil.adsScale': 'recoil multiplier while aimed',
  'recoil.crouchScale': 'recoil multiplier while crouched',

  /* ------------------------------- ergonomics ----------------------------- */
  'ergonomics.sprintOut': 's from sprint to able to fire',
  'ergonomics.swayScale': 'idle weapon sway multiplier',
  'ergonomics.adsMoveScale': 'movement speed multiplier while aimed',
  'ergonomics.switchIn': 's to draw',
  'ergonomics.switchOut': 's to holster',

  /* ---------------------------------- feel -------------------------------- */
  'viewKick.up': 'view-kick, up',
  'viewKick.back': 'view-kick, back',
  'aimSway.amp': 'rad, scoped idle sway amplitude',
  'aimSway.holdTime': 's of hold-breath available',
  'aimSway.holdScale': 'sway multiplier while holding breath',

  /* ------------------- fields the behaviour agent consumes ---------------- */
  'flinch.resist': '0..1 of incoming aim punch resisted; negative means worse',
  'signature.minimap': 'bool — does firing paint the shooter on the enemy minimap',
  'signature.radius': 'm — how far the shot is heard for minimap purposes',
  'signature.tracer': 'bool — is the tracer visible to the enemy',
  'mobility.slideFire': 'bool — may fire out of a slide',
  overheat: 'null | { capacity, perShot, coolRate, coolDelay, lockTime }',
  launcher: "null | { kind: 'grenade'|'smoke', rounds, velocity, fuse, radius }",
};

/* -------------------------------------------------------------------------- */
/* clamps                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The rails, applied after all arithmetic and before anything is derived.
 *
 * `min`/`max` are absolute; `minFrac`/`maxFrac` are relative to the weapon's own
 * base value, which is what actually stops a stacked build from running away — a
 * flat floor on `ads.in` that suits an SMG is meaningless to a sniper, but "no
 * weapon may shoulder in less than 55% of its own base time" is true of both.
 *
 * `int` rounds. Magazine capacity and burst length are counts.
 */
export const CLAMP = {
  rpm: { minFrac: 0.55, maxFrac: 1.6 },
  magSize: { min: 1, maxFrac: 3.0, int: true },
  pellets: { min: 1, int: true },
  pelletSpread: { min: 0 },
  pelletSpreadAdsScale: { min: 0.25, max: 1 },
  burstCount: { min: 2, max: 6, int: true },
  burstDelay: { min: 0 },
  pumpTime: { min: 0.22 },
  boltCycle: { min: 0.45 },

  'damage.mult': { min: 0.5, max: 1.6 },
  'damage.range': { min: 0.4, max: 2.6 },
  muzzleVelocity: { minFrac: 0.6, maxFrac: 1.9 },
  penetration: { min: 0, maxFrac: 2.2 },

  'reload.scale': { min: 0.6, max: 1.9 },
  'ads.in': { min: 0.06, minFrac: 0.55, maxFrac: 1.7 },
  'ads.out': { min: 0.05, minFrac: 0.6, maxFrac: 1.7 },

  'spread.adsMoving': { min: 0, minFrac: 0.45 },
  'spread.hip': { min: 0, minFrac: 0.45 },
  'spread.hipMoving': { min: 0, minFrac: 0.45 },
  'spread.hipSprint': { min: 0, minFrac: 0.45 },
  'spread.crouchScale': { min: 0.3, max: 1.2 },
  'spread.airScale': { min: 1.0 },
  'spread.maxBloom': { min: 0 },
  // Never zero: `perShot` is derived as `bloomDecay * (60/rpm) + bloomGain`, and a
  // decay of zero would make a blooming weapon's cone monotonically grow forever.
  'spread.bloomDecay': { min: 0.002 },
  // Never negative. A negative net gain is exactly the pinned-at-zero bug that
  // `weapons.stats.js` went out of its way to make unrepresentable, and an
  // attachment stack is the obvious way to reintroduce it.
  'spread.bloomGain': { min: 0 },

  'recoil.climb30Deg': { minFrac: 0.5, maxFrac: 1.9 },
  'recoil.yawRatio': { minFrac: 0.35, maxFrac: 2.0 },
  'recoil.random': { min: 0.02, maxFrac: 2.0 },
  'recoil.kick': { min: 0.01 },
  'recoil.recovery': { min: 1 },
  'recoil.adsScale': { min: 0.4, max: 1.25 },
  'recoil.crouchScale': { min: 0.4, max: 1.25 },

  'ergonomics.sprintOut': { min: 0.04, minFrac: 0.5, maxFrac: 1.9 },
  'ergonomics.swayScale': { min: 0.1 },
  'ergonomics.adsMoveScale': { min: 0.2, max: 1.0 },
  'ergonomics.switchIn': { min: 0.1 },
  'ergonomics.switchOut': { min: 0.08 },

  'viewKick.up': { min: 0 },
  'viewKick.back': { min: 0 },
  'aimSway.amp': { min: 0 },
  'aimSway.holdTime': { min: 0.5 },
  'aimSway.holdScale': { min: 0.01, max: 1 },

  // Negative is allowed and meaningful: a quick-ADS sniper build should flinch
  // *worse* than a stock one, which is the price the spec asks it to pay.
  'flinch.resist': { min: -0.6, max: 0.85 },
  'signature.radius': { min: 0 },
};

/**
 * Per-class clamp overrides.
 *
 * One rule lives here and it is a design guarantee rather than a safety rail: **no
 * attachment may extend or increase a shotgun's one-shot band.** Buckshot damage and
 * buckshot range are both pinned at their base values for the shotgun class, so a
 * choke can tighten a pattern and a mastery can slow the pump, but nothing in the
 * catalog can move the distance at which a shell stops killing in one. A slug
 * (`pellets === 1`) is exempt from the range half, because a slug trades the
 * one-shot away entirely — `deriveEffective` applies that exemption.
 */
export const CLASS_CLAMP = {
  sg: {
    'damage.mult': { min: 0.5, max: 1.0 },
    'damage.range': { min: 0.4, max: 1.0 },
  },
};

/**
 * The clamp table for a class, given how many projectiles the build ends up firing.
 *
 * The resolver folds `pellets` before anything else precisely so it can ask this
 * question. A shotgun still throwing shot keeps both shotgun rails; a shotgun
 * reduced to a single projectile keeps the damage rail — no shotgun build may ever
 * increase per-projectile damage — but is released from the range one, because a
 * slug has already given the one-shot kill away and the reach is the entire reason
 * to fit it.
 */
export function classClampFor(classId, pellets) {
  const c = CLASS_CLAMP[classId];
  if (!c) return null;
  if (classId === 'sg' && pellets === 1) {
    const { 'damage.range': _dropped, ...rest } = c;
    return rest;
  }
  return c;
}

/* -------------------------------------------------------------------------- */
/* path access                                                                 */
/* -------------------------------------------------------------------------- */

export function getPath(obj, path) {
  const i = path.indexOf('.');
  if (i < 0) return obj[path];
  const head = obj[path.slice(0, i)];
  return head == null ? undefined : head[path.slice(i + 1)];
}

export function setPath(obj, path, value) {
  const i = path.indexOf('.');
  if (i < 0) {
    obj[path] = value;
    return;
  }
  const key = path.slice(0, i);
  if (obj[key] == null) obj[key] = {};
  obj[key][path.slice(i + 1)] = value;
}

/** Reject a malformed modifier loudly, at module load, rather than at resolve time. */
export function validateMod(m, ownerId) {
  const where = `[attachments] ${ownerId}`;
  if (!m || typeof m.path !== 'string') throw new Error(`${where}: modifier has no path`);
  if (!(m.path in PATHS)) throw new Error(`${where}: "${m.path}" is not an addressable stat`);
  if (!MOD_KINDS.includes(m.kind)) throw new Error(`${where}: "${m.kind}" is not a modifier kind`);
  if (m.kind !== 'set' && typeof m.value !== 'number') {
    throw new Error(`${where}: ${m.kind} on "${m.path}" needs a number`);
  }
  return m;
}

/* -------------------------------------------------------------------------- */
/* the calculation                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Fold a bag of modifiers onto one path.
 *
 * @param base    the assembled weapon's own value
 * @param bag     { add, mult, scale, set, hasSet } accumulated for this path
 * @param clamp   the clamp entry for this path, or undefined
 * @returns the resolved value
 */
export function foldPath(base, bag, clamp) {
  if (bag.hasSet) return clampValue(bag.set, base, clamp);
  if (typeof base !== 'number') return base;
  let v = base + bag.add;
  v *= 1 + bag.mult;
  v *= bag.scale;
  return clampValue(v, base, clamp);
}

export function clampValue(v, base, clamp) {
  if (!clamp || typeof v !== 'number') return v;
  let lo = clamp.min ?? -Infinity;
  let hi = clamp.max ?? Infinity;
  if (typeof base === 'number' && base !== 0) {
    // Fractional rails are read against |base| so a negative base (none today, but
    // `flinch.resist` could authored as one tomorrow) does not invert them.
    const b = Math.abs(base);
    if (clamp.minFrac != null) lo = Math.max(lo, b * clamp.minFrac * Math.sign(base || 1));
    if (clamp.maxFrac != null) hi = Math.min(hi, b * clamp.maxFrac * Math.sign(base || 1));
  }
  let out = Math.min(hi, Math.max(lo, v));
  if (clamp.int) out = Math.round(out);
  return out;
}

/** A fresh, empty accumulator for one path. */
export function emptyBag() {
  return { add: 0, mult: 0, scale: 1, set: undefined, hasSet: false, setFrom: -1 };
}

/**
 * Accumulate one modifier into a per-path bag map.
 *
 * `slotRank` is the modifier's slot position in `SLOT_ORDER`; a later slot's `set`
 * overrides an earlier one's, which is the only place equipment order could
 * otherwise leak into the result.
 */
export function accumulate(bags, mod, slotRank) {
  let bag = bags.get(mod.path);
  if (!bag) {
    bag = emptyBag();
    bags.set(mod.path, bag);
  }
  switch (mod.kind) {
    case 'add':
      bag.add += mod.value;
      break;
    case 'mult':
      bag.mult += mod.value;
      break;
    case 'scale':
      bag.scale *= mod.value;
      break;
    case 'set':
      if (slotRank >= bag.setFrom) {
        bag.set = mod.value;
        bag.hasSet = true;
        bag.setFrom = slotRank;
      }
      break;
    default:
      break;
  }
  return bags;
}
